import React, { FormEvent, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import NumberFormat from 'react-number-format';
import Dialog from 'components/Dialog';
import WeightInputField from 'components/CustomInputs/WeightInputField';
import { ClosureStatusEnum, ConditionedStock, DialogWidth, InventoryFormInterface, Material } from 'types';
import theme, { colors, fonts } from 'theme';
import { useClosureByMonthAndYear, useConditionedStocks, useDistributionKeys } from 'hooks';
import IntegerNumberFormat from 'components/CustomInputs/IntegerNumberFormat';
import Loading from 'components/Loading';
import { isMaterialBulk } from 'utils/formValidation';
import { roundToPrecision } from 'services/math';
import { ClosureInventoryStateEnum } from 'pages/Closures/types';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { putClosureInventoryState } from 'services/apiOperations';
import { formatMonthForDisplay } from 'utils/date';
import { inventoryFormSchema } from './InventoryForm.yup';
import { useLatestClosureDate } from '../../utils/closureHooks';
import { AppContext } from '../../context';
import { notify } from '../Notifier';
import CancelIcon from '../Icons/CancelIcon';
import { ValidateIcon } from '../Icons';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    paddingBottom: '2.5em',
  },
  fieldLine: {
    alignSelf: 'flex-start',
    marginTop: 8,
    marginBottom: 64,

    '& > div': {
      minWidth: 470,
    },

    '& span': {
      fontSize: 18,
      marginBottom: 5,
    },
  },
  sharedLine: {
    display: 'flex',
    justifyContent: 'space-between',

    '& > div': {
      width: 214,
    },
  },
  materialTable: {
    padding: '15px',
    overflow: 'hidden',
  },
  headerCell: {
    padding: '11px 22px',
    borderTop: 'none',
    borderBottom: 'none',
    '&:not(:last-child)': {
      borderRight: `1px solid ${colors.greyLighten20}`,
    },
  },
  bodyCell: {
    padding: '10px 22px',
    border: `1px solid ${colors.greyLighten20}`,
    '&:first-child': {
      borderLeft: 'none',
    },
    '&:last-child': {
      borderRight: 'none',
    },
  },
  disabled: {
    color: colors.grey500,
  },
  muiTextFieldRoot: {
    '& input': {
      textAlign: 'right',
      fontFamily: fonts.secondary,
    },
  },
  alignRight: {
    textAlign: 'right',
  },
  alignSelfEnd: {
    alignSelf: 'end',
  },
  icon: {
    color: theme.palette.text.primary,
    fontSize: 'medium',
    fontWeight: 600,
    marginLeft: '6px',
  },
  button: {
    display: 'flex',
    alignItems: 'end',
    padding: '10px',
    margin: '0 10px',
  },
  buttonLine: {
    display: 'flex',
    marginTop: '25px',
    marginBottom: '15px',
    justifyContent: 'center',
  },
});

const StyledTableCell = withStyles({
  root: {
    fontFamily: fonts.secondary,
    fontSize: '16px',
  },
})(TableCell);

interface InventoryWeightProps {
  control: any;
  setValue: any;
  index: number;
  disabled: boolean;
  material: Material;
}

const TotalMaterialInventoryWeightField = ({ control, index, material, setValue, disabled }: InventoryWeightProps) => {
  const classes = useStyles();
  const conditioningQuantity = useWatch({
    control,
    name: `conditionedStocks.${index}.inventoryConditioningQuantity`,
  });

  const inventoriedAverageBallWeight = useWatch({
    control,
    name: `conditionedStocks.${index}.inventoryAverageBallWeight`,
  });

  const averageBallWeight = inventoriedAverageBallWeight ?? material.averageWeight;

  const totalMaterialWeight =
    isMaterialBulk(material) || null === averageBallWeight ? null : averageBallWeight * conditioningQuantity;

  useEffect(() => {
    setValue(`conditionedStocks.${index}.inventoryWeight`, totalMaterialWeight ? totalMaterialWeight : 0);
  }, [index, setValue, totalMaterialWeight]);

  return <WeightInputField className={classes.muiTextFieldRoot} value={totalMaterialWeight ?? 0} disabled />;
};

export interface InventoryFormProps {
  onSubmit: SubmitHandler<InventoryFormInterface>;
  formName?: string;
  year: string;
  month: string;
  isClosedMonth: boolean;
  inventoryEditable?: boolean;
  resetForm?: boolean;
  resetFormValues?: ConditionedStock[];
  isDialogReset?: boolean;
}

let conditionedStocksInitialValue: ConditionedStock[] = [];

const computeCalculatedTonnage = (conditionedStock: ConditionedStock, isClosedMonth: boolean) => {
  if (conditionedStock.isMaterialBulk) {
    return conditionedStock.weightInKilos / 1000; // result <- weight in tons
  } else if (isClosedMonth) {
    // Does not compute calculated tonnage but get value from closed month inventory
    return (
      (roundToPrecision(conditionedStock.conditioningQuantity) *
        roundToPrecision(conditionedStock.inventoryCalculatedAverageBallWeightInKilos, 0)) /
      1000
    ); // result <- weight in tons
  }
  // Material is ball or package
  return (
    (roundToPrecision(conditionedStock.conditioningQuantity) *
      roundToPrecision(conditionedStock.calculatedAverageBallWeightInKilos, 0)) /
    1000
  ); // result <- computed weight in tons
};

const computeCalculatedAverageBallWeight = (conditionedStock: ConditionedStock) => {
  return roundToPrecision(conditionedStock.calculatedAverageBallWeightInKilos / 1000); // result <- average ball weight computed from API, in tons
};

const InventoryForm: React.FC<InventoryFormProps> = ({
  onSubmit,
  formName,
  year,
  month,
  isClosedMonth,
  inventoryEditable = false,
  resetForm = false,
  resetFormValues = [],
  isDialogReset = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'inventoryForm' });

  const conditionedStocks = useConditionedStocks(year, month);
  const schema = useMemo(() => inventoryFormSchema(t), [t]);
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const [closureMonth] = useLatestClosureDate(sortingCenter);
  const [distributionKeys] = useDistributionKeys(
    String(sortingCenter?.['@id']),
    closureMonth ? closureMonth : undefined,
  );
  const currentClosure = useClosureByMonthAndYear(year, month, String(sortingCenter?.['@id']));
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const filteredConditionedStocks = conditionedStocks?.filter(conditionedStock => {
    return distributionKeys?.some(
      distributionKey =>
        distributionKey.material['@id'] === conditionedStock.materialObject['@id'] &&
        distributionKey.distributionKey > 0,
    );
  });

  const {
    control,
    handleSubmit,
    register,
    setValue,
    setError,
    reset,
    formState: { errors },
  } = useForm<InventoryFormInterface>({
    mode: 'onBlur',
    defaultValues: {
      conditionedStocks: [],
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (
      isDialogReset &&
      currentClosure &&
      currentClosure.status === ClosureStatusEnum.REOPEN &&
      [ClosureInventoryStateEnum.NO_CHOICE, ClosureInventoryStateEnum.REINIT].includes(
        currentClosure.inventoriedOutboundStockState,
      )
    ) {
      setIsDialogOpen(true);
    }
  }, [currentClosure, isDialogReset]);

  useEffect(() => {
    if (resetFormValues.length > 0) {
      conditionedStocksInitialValue = resetFormValues;
    } else {
      conditionedStocksInitialValue = filteredConditionedStocks.map((conditionedStock: ConditionedStock) => {
        return {
          ...conditionedStock,
          inventoryWeight: conditionedStock.inventoryWeight / 1000,
          inventoryAverageBallWeight: conditionedStock.inventoryAverageBallWeight / 1000,
        };
      });
    }
  }, [filteredConditionedStocks, resetFormValues]);

  useEffect(() => {
    if (resetForm) {
      reset({
        conditionedStocks: conditionedStocksInitialValue,
      });
    }
  }, [resetForm, reset]);

  useEffect(() => {
    if (inventoryEditable) setCheckboxChecked(false);
  }, [inventoryEditable]);

  const [checkboxChecked, setCheckboxChecked] = useState(false);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let nbWeightInKilosNegatives = 0;
    if (event.target.checked) {
      filteredConditionedStocks.map((conditionedStock: ConditionedStock, index) => {
        if (!conditionedStock.isMaterialBulk) {
          if (conditionedStock.conditioningQuantity < 0) {
            nbWeightInKilosNegatives += 1;
            setError(`conditionedStocks.${index}.inventoryConditioningQuantity`, {
              type: 'error',
              message: t('validation.inventoryConditioningQuantity.required'),
            });
            setValue(`conditionedStocks.${index}.inventoryConditioningQuantity`, '' as any);
          } else {
            setValue(`conditionedStocks.${index}.inventoryConditioningQuantity`, conditionedStock.conditioningQuantity);
            setValue(
              `conditionedStocks.${index}.inventoryAverageBallWeight`,
              computeCalculatedAverageBallWeight(conditionedStock),
            );
          }
        } else {
          if (conditionedStock.weightInKilos < 0) {
            nbWeightInKilosNegatives += 1;
            setError(`conditionedStocks.${index}.inventoryWeight`, {
              type: 'error',
              message: t('validation.inventoryWeight.required'),
            });
            setValue(`conditionedStocks.${index}.inventoryWeight`, '' as any);
          } else {
            setValue(`conditionedStocks.${index}.inventoryWeight`, conditionedStock.weightInKilos / 1000);
          }
        }
        return null;
      });
      if (nbWeightInKilosNegatives > 0) {
        if (nbWeightInKilosNegatives === 1) {
          notify({
            message: t('validation.notification.error.negative'),
            severity: 'error',
          });
        } else {
          notify({
            message: t('validation.notification.error.negatives'),
            severity: 'error',
          });
        }
      }
    } else {
      reset({
        conditionedStocks: conditionedStocksInitialValue,
      });
    }
    setCheckboxChecked(!checkboxChecked);
  };

  const setAllFormValuesToZero = () => {
    filteredConditionedStocks.map((conditionedStock: ConditionedStock, index) => {
      setValue(`conditionedStocks.${index}.inventoryConditioningQuantity`, 0);
      setValue(`conditionedStocks.${index}.inventoryWeight`, 0);
      return null;
    });
  };

  const handleDialog = (choice: ClosureInventoryStateEnum) => {
    if (ClosureInventoryStateEnum.REINIT === choice) {
      setAllFormValuesToZero();
    }

    putClosureInventoryState(transformObjectId(currentClosure), choice);
  };

  const handleReinitStock = (reinit: boolean) => {
    handleDialog(reinit ? ClosureInventoryStateEnum.REINIT : ClosureInventoryStateEnum.NOT_REINIT);
    setIsDialogOpen(false);
  };

  const handleSubmitInventoryForm = (e: FormEvent) => {
    e.preventDefault();
    putClosureInventoryState(transformObjectId(currentClosure), ClosureInventoryStateEnum.NOT_REINIT);
    handleSubmit(onSubmit)();
  };

  return (
    <form id={formName} onSubmit={handleSubmitInventoryForm} className={classes.form}>
      {inventoryEditable && (
        <div className={classes.alignSelfEnd}>
          <FormControlLabel
            value="start"
            control={
              <Checkbox checked={checkboxChecked} color={'primary'} onChange={event => handleCheckboxChange(event)} />
            }
            label={t('copyValuesForInventory')}
            labelPlacement="start"
          />
        </div>
      )}
      <Dialog
        closable={false}
        renderButton={() => {}}
        isOpen={isDialogOpen}
        width={DialogWidth.LARGE_FORM}
        renderContent={() => (
          <>
            <Typography variant="h2">
              {t('dialog.updateInventory.title', {
                month: formatMonthForDisplay(Number(month), Number(year)),
              })}
            </Typography>
            <div className={classes.buttonLine}>
              <Button variant="outlined" className={classes.button} onClick={() => handleReinitStock(true)}>
                {t('dialog.updateInventory.back')}
                <CancelIcon className={classes.icon} />
              </Button>
              <Button variant="outlined" className={classes.button} onClick={() => handleReinitStock(false)}>
                {t('dialog.updateInventory.submit')}
                <ValidateIcon className={classes.icon} />
              </Button>
            </div>
          </>
        )}
      />
      {currentClosure && !isDialogOpen && filteredConditionedStocks.length > 0 ? (
        <TableContainer className={classes.materialTable}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell className={classes.headerCell}>{t('material')}</StyledTableCell>
                <StyledTableCell className={classes.headerCell}>{t('calculatedBalls')}</StyledTableCell>
                <StyledTableCell className={classes.headerCell}>{t('calculatedAverageBallWeight')}</StyledTableCell>
                <StyledTableCell className={classes.headerCell}>{t('calculatedTonnage')}</StyledTableCell>
                <StyledTableCell className={classes.headerCell}>{t('inventoriedBalls')}</StyledTableCell>
                <StyledTableCell className={classes.headerCell}>{t('inventoriedAverageWeight')}</StyledTableCell>
                <StyledTableCell className={classes.headerCell}>{t('inventoriedTonnage')}</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredConditionedStocks.map((conditionedStock: ConditionedStock, index) => (
                <TableRow key={`conditionedStocks.${conditionedStock.material}`}>
                  <StyledTableCell className={classes.bodyCell}>{conditionedStock.materialName}</StyledTableCell>
                  <StyledTableCell className={classes.bodyCell}>
                    {conditionedStock.isMaterialBulk ? '-' : conditionedStock.conditioningQuantity}
                  </StyledTableCell>
                  <StyledTableCell className={classes.bodyCell}>
                    {conditionedStock.isMaterialBulk ? (
                      '-'
                    ) : (
                      <NumberFormat
                        decimalSeparator=","
                        defaultValue={computeCalculatedAverageBallWeight(conditionedStock)}
                        decimalScale={3}
                        isNumericString
                        suffix=" t"
                        fixedDecimalScale={true}
                        displayType="text"
                      />
                    )}
                  </StyledTableCell>
                  <StyledTableCell className={classes.bodyCell}>
                    <NumberFormat
                      decimalSeparator=","
                      decimalScale={3}
                      isNumericString
                      suffix=" t"
                      fixedDecimalScale={true}
                      displayType="text"
                      value={computeCalculatedTonnage(conditionedStock, isClosedMonth)}
                    />
                  </StyledTableCell>
                  <StyledTableCell className={classes.bodyCell}>
                    <Controller
                      name={`conditionedStocks.${index}.inventoryConditioningQuantity`}
                      control={control}
                      defaultValue={conditionedStock.inventoryConditioningQuantity}
                      shouldUnregister
                      render={({ field: { ref, ...inputProps }, fieldState }) => (
                        <>
                          {conditionedStock.isMaterialBulk ? (
                            '-'
                          ) : (
                            <TextField
                              className={classes.muiTextFieldRoot}
                              inputRef={ref}
                              {...inputProps}
                              helperText={fieldState.error?.message}
                              error={fieldState.invalid}
                              disabled={!inventoryEditable || conditionedStock.isMaterialBulk}
                              InputProps={{
                                inputComponent: IntegerNumberFormat as any,
                              }}
                            />
                          )}
                        </>
                      )}
                    />
                  </StyledTableCell>
                  <StyledTableCell className={classes.bodyCell}>
                    {conditionedStock.isMaterialBulk ? (
                      '-'
                    ) : (
                      <Controller
                        name={`conditionedStocks.${index}.inventoryAverageBallWeight`}
                        control={control}
                        defaultValue={roundToPrecision(conditionedStock.inventoryAverageBallWeight / 1000)}
                        shouldUnregister
                        render={({ field: { ref, ...inputProps }, fieldState }) => (
                          <WeightInputField
                            className={classes.muiTextFieldRoot}
                            inputRef={ref}
                            disabled={!inventoryEditable}
                            {...inputProps}
                            helperText={fieldState.error?.message}
                            error={fieldState.invalid}
                          />
                        )}
                      />
                    )}
                  </StyledTableCell>
                  <StyledTableCell className={classes.bodyCell}>
                    <input
                      hidden
                      {...register(`conditionedStocks.${index}.inventoryIdApiPlatform`, {
                        shouldUnregister: true,
                        value: conditionedStock.inventoryIdApiPlatform,
                      })}
                    />
                    <input
                      hidden
                      {...register(`conditionedStocks.${index}.material`, {
                        shouldUnregister: true,
                        value: conditionedStock.material,
                      })}
                    />
                    <input
                      hidden
                      {...register(`conditionedStocks.${index}.sortingCenter`, {
                        shouldUnregister: true,
                        value: conditionedStock.sortingCenter,
                      })}
                    />
                    <input
                      hidden
                      {...register(`conditionedStocks.${index}.inventoryMonth`, {
                        shouldUnregister: true,
                        value: month,
                      })}
                    />
                    <input
                      hidden
                      {...register(`conditionedStocks.${index}.inventoryYear`, {
                        shouldUnregister: true,
                        value: year,
                      })}
                    />
                    <input
                      hidden
                      {...register(`conditionedStocks.${index}.isMaterialBulk`, {
                        shouldUnregister: true,
                        value: conditionedStock.isMaterialBulk,
                      })}
                    />
                    <input
                      hidden
                      {...register(`conditionedStocks.${index}.calculatedAverageBallWeightInKilos`, {
                        shouldUnregister: true,
                        value: conditionedStock.calculatedAverageBallWeightInKilos,
                      })}
                    />
                    {conditionedStock.isMaterialBulk ? (
                      <Controller
                        name={`conditionedStocks.${index}.inventoryWeight`}
                        control={control}
                        defaultValue={conditionedStock.inventoryWeight / 1000}
                        shouldUnregister
                        render={({ field: { ref, ...inputProps }, fieldState }) => (
                          <WeightInputField
                            className={classes.muiTextFieldRoot}
                            inputRef={ref}
                            disabled={!inventoryEditable || !conditionedStock.isMaterialBulk}
                            {...inputProps}
                            helperText={fieldState.error?.message}
                            error={fieldState.invalid}
                          />
                        )}
                      />
                    ) : (
                      <TotalMaterialInventoryWeightField
                        control={control}
                        disabled={!inventoryEditable}
                        index={index}
                        material={conditionedStock.materialObject}
                        setValue={setValue}
                      />
                    )}
                  </StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {
            // @ts-ignore errors object is wrongly typed by react-hook-form. It expects an array while it is an object
            errors?.conditionedStocks?.type && errors.conditionedStocks.type === 'atLeastOne' && (
              // @ts-ignore
              <p className={classes.errorAtLeastOne}>{t(errors.conditionedStocks.message)}</p>
            )
          }
        </TableContainer>
      ) : (
        <Loading />
      )}
    </form>
  );
};

export default InventoryForm;
